import { Spin } from 'antd'
import { useQuantitativeDataSummaryStats } from 'features/dataLayers'
import { FigureSelectionArea } from 'features/fde'
import { IndicationItem } from 'features/indicationTriage/api/indicationTriageApi'
import {
  LABEL_WIDTH,
  SectionContainer,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { FfsParameterSwitch } from 'features/indicationTriage/cards/ffsCalc/FfsParameterSwitch'
import { WORKING_UNIT_THICKNESS } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { useWindowFrameCdf } from 'features/indicationTriage/cards/ffsCalc/useWindowFrame'
import { useCroppedCdfSummaryStats } from 'features/indicationTriage/cards/indicationOverview/useCroppedCdfSummaryStats'
import _, { isNil } from 'lodash'
import React, { Dispatch, FC, SetStateAction, useMemo, useState } from 'react'
import {
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
  GeckoPipeSection,
} from 'types'

export const LABEL_TMM = (
  <>
    t<sub>mm</sub>
  </>
)

export const LABEL_TRD = (
  <>
    t<sub>rd</sub>
  </>
)

export const LABEL_TC = (
  <>
    t<sub>c</sub>
  </>
)

export const LABEL_D_INNER = (
  <>
    D<sub>inner</sub>
  </>
)

const GET_DIAMTERS: Partial<
  Record<
    GeckoComponentModelType,
    (
      component: GeckoComponentModel,
      tmm: GeckoMeasurement | null,
      innerCorrosion: boolean
    ) => [GeckoMeasurement, GeckoMeasurement, GeckoMeasurement | null]
  >
> = {
  [GeckoComponentModelType.PipeSection]: (component, tmm, innerCorrosion) => {
    const pipeComponent = component as GeckoPipeSection
    const outerDiameter = GeckoMeasurement.CreateFromValue(
      pipeComponent.radius.num * 2,
      pipeComponent.radius.unit
    )
    const innerDiameter = GeckoMeasurement.CreateFromValue(
      outerDiameter.num -
        2 *
          (pipeComponent.wallNominalThickness?.convertTo(outerDiameter.unit)
            .num ?? 0),
      outerDiameter.unit
    )

    const innerDiameterWithCorrosion = tmm
      ? GeckoMeasurement.CreateFromValue(
          innerDiameter.num +
            (pipeComponent.wallNominalThickness?.convertTo(innerDiameter.unit)
              .num ?? 0) -
            tmm.convertTo(innerDiameter.unit).num,
          innerDiameter.unit
        )
      : innerDiameter

    return [
      innerCorrosion ? innerDiameterWithCorrosion : innerDiameter,
      outerDiameter,
      pipeComponent.wallNominalThickness ?? null,
    ]
  },
}

const LONGITUDINAL_AXIS_LOOKUP: Record<GeckoComponentModelType, 'x' | 'y'> = {
  [GeckoComponentModelType.VerticalTankShell]: 'x',
  [GeckoComponentModelType.TankFloor]: 'x',
  [GeckoComponentModelType.TankFlatRoof]: 'x',
  [GeckoComponentModelType.HorizontalTankShell]: 'x',
  [GeckoComponentModelType.Cone]: 'x',
  [GeckoComponentModelType.PipeSection]: 'y',
  [GeckoComponentModelType.LegacyBoilerWalls]: 'x',
  [GeckoComponentModelType.ShipDeck]: 'x',
  [GeckoComponentModelType.BasicShipHull]: 'x',
  [GeckoComponentModelType.LegacyCone]: 'x',
  [GeckoComponentModelType.LaunchTube]: 'x',
  [GeckoComponentModelType.Dome]: 'x',
  [GeckoComponentModelType.DomeWall]: 'x',
  [GeckoComponentModelType.FlatCircularFloor]: 'x',
  [GeckoComponentModelType.VerticalTankFoundation]: 'x',
  [GeckoComponentModelType.MachinedForgingSimple]: 'x',
  [GeckoComponentModelType.PressureVesselShell]: 'x',
  [GeckoComponentModelType.HemisphericalHead]: 'x',
  [GeckoComponentModelType.TorisphericalHead]: 'x',
  [GeckoComponentModelType.EllipsoidalHead]: 'x',
}

function getLength(measurementRange: GeckoMeasurementRange): GeckoMeasurement {
  return GeckoMeasurement.CreateFromValue(
    measurementRange.max.num - measurementRange.min.num,
    measurementRange.unit
  )
}

function getFlawLengths(
  longitundinalAxis: 'x' | 'y',
  figureSelectionArea: FigureSelectionArea
): [GeckoMeasurement, GeckoMeasurement] {
  const lenX = getLength(figureSelectionArea.x)
  const lenY = getLength(figureSelectionArea.y)
  return longitundinalAxis === 'x' ? [lenX, lenY] : [lenY, lenX]
}

interface FfsDataValueSectionProps {
  loading: boolean
  tmm: GeckoMeasurement | null
  trd: GeckoMeasurement | null
  tc: GeckoMeasurement | null
  longitudinalFlawLength: GeckoMeasurement | null
  latitudinalFlawLength: GeckoMeasurement | null
  innerDiameter: GeckoMeasurement | null
  outerDiameter: GeckoMeasurement | null
  lambda: GeckoMeasurement | null
  nominal: GeckoMeasurement | null
  corrosionOnInnerSurface: boolean
  setCorrosionOnInnerSurface: Dispatch<SetStateAction<boolean>>
}

type HookParams = {
  indicationItem: IndicationItem | undefined
  componentModel: GeckoComponentModel | undefined
  thicknessCdf: GeckoComponentDataFrame | undefined
  fca: GeckoMeasurement | null
}

export function getLongitudinalAxis(
  componentModel: GeckoComponentModel | undefined
): 'x' | 'y' {
  if (!componentModel) {
    return 'x'
  }
  const cType = componentModel.type
  return LONGITUDINAL_AXIS_LOOKUP[cType] ?? 'x'
}

export function computeLambda(
  innerDiameter: GeckoMeasurement,
  s: GeckoMeasurement,
  tc: GeckoMeasurement
): GeckoMeasurement {
  return GeckoMeasurement.CreateFromValue(
    (1.285 * s.convertTo(WORKING_UNIT_THICKNESS).num) /
      Math.sqrt(
        innerDiameter.convertTo(WORKING_UNIT_THICKNESS).num *
          tc.convertTo(WORKING_UNIT_THICKNESS).num
      ),
    GeckoMeasurementUnit.Ratio
  )
}

export function useFfsLevelOneDataValues({
  indicationItem,
  fca,
  componentModel,
  thicknessCdf,
}: HookParams): FfsDataValueSectionProps {
  const { stats: cdfStatsThickness, isLoading: cdfStatsThicknessLoading } =
    useCroppedCdfSummaryStats(thicknessCdf, indicationItem?.selection)
  const tmm = cdfStatsThickness?.value_range.min ?? null

  // We take a 'window frame' around the indication to get a trd
  const { cdf: windowFrameCdf, isLoading: windowFrameLoading } =
    useWindowFrameCdf(thicknessCdf, indicationItem?.selection, 3)
  const { data: windowFrameStats, isLoading: windowFrameStatsLoading } =
    useQuantitativeDataSummaryStats(windowFrameCdf ?? undefined)

  const [corrosionOnInnerSurface, setCorrosionOnInnerSurface] =
    useState<boolean>(false)

  const loading =
    windowFrameLoading || cdfStatsThicknessLoading || windowFrameStatsLoading
  const trd = windowFrameStats?.value_range.min ?? null

  const tc = useMemo(
    () =>
      _.isNil(trd) || _.isNil(fca)
        ? null
        : GeckoMeasurement.CreateFromValue(
            trd.convertTo(WORKING_UNIT_THICKNESS).num -
              fca.convertTo(WORKING_UNIT_THICKNESS).num,
            WORKING_UNIT_THICKNESS
          ),
    [fca, trd]
  )

  const longitundinalAxis: 'x' | 'y' = useMemo(
    () => getLongitudinalAxis(componentModel),
    [componentModel]
  )
  const [longitudinalFlawLength, latitudinalFlawLength] = useMemo(
    () =>
      indicationItem?.selection
        ? getFlawLengths(longitundinalAxis, indicationItem?.selection)
        : [null, null],
    [indicationItem, longitundinalAxis]
  )

  const [innerDiameter, outerDiameter, nominal]: [
    GeckoMeasurement | null,
    GeckoMeasurement | null,
    GeckoMeasurement | null,
  ] = useMemo(
    () =>
      componentModel
        ? (GET_DIAMTERS[componentModel.type]?.(
            componentModel,
            tmm,
            corrosionOnInnerSurface
          ) ?? [null, null, null])
        : [null, null, null],
    [componentModel, corrosionOnInnerSurface, tmm]
  )

  const lambda = useMemo(
    () =>
      isNil(longitudinalFlawLength) || isNil(innerDiameter) || isNil(tc)
        ? null
        : computeLambda(innerDiameter, longitudinalFlawLength, tc),
    [tc, innerDiameter, longitudinalFlawLength]
  )

  return useMemo(
    () => ({
      loading,
      tmm,
      trd,
      tc,
      longitudinalFlawLength,
      latitudinalFlawLength,
      innerDiameter,
      outerDiameter,
      lambda,
      nominal,
      corrosionOnInnerSurface,
      setCorrosionOnInnerSurface,
    }),
    [
      innerDiameter,
      lambda,
      loading,
      longitudinalFlawLength,
      latitudinalFlawLength,
      outerDiameter,
      tc,
      tmm,
      trd,
      nominal,
      corrosionOnInnerSurface,
      setCorrosionOnInnerSurface,
    ]
  )
}

const RawFfsDataValueSection: FC<FfsDataValueSectionProps> = ({
  loading,
  tmm,
  trd,
  tc,
  longitudinalFlawLength,
  latitudinalFlawLength,
  innerDiameter,
  lambda,
  nominal,
  corrosionOnInnerSurface,
  setCorrosionOnInnerSurface,
}) => {
  return (
    <Spin spinning={loading}>
      <SectionContainer>
        <FfsParameterSwitch
          label='Thinning surface'
          labelWidth={LABEL_WIDTH}
          value={corrosionOnInnerSurface}
          setValue={setCorrosionOnInnerSurface}
          checkedContent='Inner Surface'
          uncheckedContent='Outer Surface'
          labelTooltip='Adjust inner diameter for corrosion'
        />
        <FfsParameterField
          label={LABEL_TMM}
          labelTooltip='Minimum Measured within corrosion area'
          labelWidth={LABEL_WIDTH}
          value={tmm}
        />
        <FfsParameterField
          label={'Nominal'}
          labelTooltip='Nominal thickness'
          labelWidth={LABEL_WIDTH}
          value={nominal}
        />
        <FfsParameterField
          label={LABEL_TRD}
          labelTooltip='uniform thickness away from the local metal loss'
          labelWidth={LABEL_WIDTH}
          value={trd ?? null}
        />
        <FfsParameterField
          label={LABEL_TC}
          labelTooltip='Future corroded wall thickness away from damage area'
          labelWidth={LABEL_WIDTH}
          value={tc}
        />
        <FfsParameterField
          label='s'
          labelTooltip='Longitudinal length of metal loss region'
          labelWidth={LABEL_WIDTH}
          value={longitudinalFlawLength}
        />
        <FfsParameterField
          label='c'
          labelTooltip='Circumferential length of metal loss region'
          labelWidth={LABEL_WIDTH}
          value={latitudinalFlawLength}
        />
        <FfsParameterField
          label={LABEL_D_INNER}
          labelTooltip='Inner Pipe Diameter (nominal)'
          labelWidth={LABEL_WIDTH}
          value={innerDiameter}
        />
        <FfsParameterField
          label={<>&lambda;</>}
          labelTooltip={
            <>
              1.285s / sqrt({LABEL_D_INNER} {LABEL_TC})
            </>
          }
          labelWidth={LABEL_WIDTH}
          value={lambda}
        />
      </SectionContainer>
    </Spin>
  )
}

export const FfsDataValueSection = React.memo(RawFfsDataValueSection)
